import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  InputAdornment,
  Fade,
  Card,
  CardContent,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd,
  Email as EmailIcon,
  Lock,
  Person,
  LocationOn,
  Refresh,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { useSnackbar } from "notistack";

/* -------------------------
   Styled containers & comps
   ------------------------- */
const BackgroundContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundImage:
    "url(https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundAttachment: "fixed",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.45)",
    zIndex: 1,
  },
}));

const LoginCard = styled(Card)(({ theme }) => ({
  maxWidth: 520,
  width: "100%",
  zIndex: 2,
  position: "relative",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(18,18,18,0.95)" : "rgba(255,255,255,0.95)",
  backdropFilter: "blur(10px)",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.25),
  borderRadius: theme.spacing(1),
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.getContrastText(theme.palette.primary.main),
  "&:hover": {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`auth-tabpanel-${index}`} aria-labelledby={`auth-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/* -------------------------
   Main Component
   ------------------------- */
function SimpleLogin() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    login,
    verifyOtp,
    signup,
    verifySignupOtp,
    otpRequired,
    loginEmail,
    signupOtpRequired,
    signupEmail,
    error: authError,
    clearError,
    // optional: forgotPassword may or may not exist in your AuthContext
    forgotPassword,
  } = useAuth();

  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    username: "",
    password: "",
    location: "",
  });
  const [otp, setOtp] = useState("");
  const [signupOtp, setSignupOtp] = useState("");

  // Forgot password dialog state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState('email'); // 'email' or 'reset'
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetForm, setResetForm] = useState({
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showResetPasswords, setShowResetPasswords] = useState({
    new: false,
    confirm: false,
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError("");
    clearError && clearError();
    setSignupOtp("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  /* ----- Login flow ----- */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    clearError && clearError();
    setIsLoginLoading(true);

    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      setIsLoginLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password);
      if (result && result.success) {
        if (!result.requiresOtp) {
          navigate("/filesystem");
        }
        // If OTP is required, the context will update state and the UI should show OTP input (managed by otpRequired from context or local state if needed)
      } else {
        setError(result?.error || "Login failed. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "Login failed. Please try again.");
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    clearError && clearError();
    setIsLoginLoading(true);

    if (!otp) {
      setError("Please enter the OTP");
      setIsLoginLoading(false);
      return;
    }

    try {
      const result = await verifyOtp(loginEmail, otp);
      if (result && result.success) {
        navigate("/filesystem");
      } else {
        setError(result?.error || "OTP verification failed. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "OTP verification failed. Please try again.");
    } finally {
      setIsLoginLoading(false);
    }
  };

  /* ----- Signup flow ----- */
  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    clearError && clearError();
    setIsSignupLoading(true);

    if (!formData.name || !formData.username || !formData.password || !formData.location) {
      setError("Please fill in all fields including location");
      setIsSignupLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      setIsSignupLoading(false);
      return;
    }

    try {
      const signupData = { ...formData, email: `${formData.username}@stefto.com` };
      const result = await signup(signupData);
      if (!(result && result.success)) {
        setError(result?.error || "Signup failed. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "Signup failed. Please try again.");
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleVerifySignupOtp = async (e) => {
    e.preventDefault();
    setError("");
    clearError && clearError();
    setIsSignupLoading(true);

    if (!signupOtp) {
      setError("Please enter the OTP");
      setIsSignupLoading(false);
      return;
    }

    try {
      const result = await verifySignupOtp(signupEmail, signupOtp);
      if (result && result.success) {
        navigate("/filesystem");
      } else {
        setError(result?.error || "Signup OTP verification failed. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "Signup OTP verification failed. Please try again.");
    } finally {
      setIsSignupLoading(false);
    }
  };

  /* ----- Forgot password ----- */
  const openForgot = () => {
    setForgotOpen(true);
    setForgotStep('email');
    setForgotEmail("");
    setResetForm({
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotStep('email');
    setForgotEmail("");
    setResetForm({
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleForgotEmailSubmit = async () => {
    if (!forgotEmail.trim()) {
      enqueueSnackbar("Please enter your email address.", { variant: 'warning' });
      return;
    }

    setForgotLoading(true);
    try {
      const response = await api.post('/api/users/forgot-password', {
        email: forgotEmail.trim(),
      });

      if (response.data.success) {
        enqueueSnackbar("OTP sent to your email. Please check your inbox.", { variant: 'success' });
        setForgotStep('reset');
      } else {
        enqueueSnackbar(response.data.message || "Failed to send reset OTP.", { variant: 'error' });
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      enqueueSnackbar(
        error.response?.data?.message || "Failed to send reset OTP. Please try again.",
        { variant: 'error' }
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    // Validation
    if (!resetForm.otp.trim() || !resetForm.newPassword || !resetForm.confirmPassword) {
      enqueueSnackbar("Please fill in all fields.", { variant: 'warning' });
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      enqueueSnackbar("New passwords do not match.", { variant: 'error' });
      return;
    }

    if (resetForm.newPassword.length < 6) {
      enqueueSnackbar("New password must be at least 6 characters long.", { variant: 'warning' });
      return;
    }

    setForgotLoading(true);
    try {
      const response = await api.post('/api/users/reset-password', {
        email: forgotEmail.trim(),
        otp: resetForm.otp.trim(),
        newPassword: resetForm.newPassword,
        confirmPassword: resetForm.confirmPassword,
      });

      if (response.data.success) {
        enqueueSnackbar("Password reset successfully! You can now sign in with your new password.", { variant: 'success' });
        closeForgot();
      } else {
        enqueueSnackbar(response.data.message || "Failed to reset password.", { variant: 'error' });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      enqueueSnackbar(
        error.response?.data?.message || "Failed to reset password. Please try again.",
        { variant: 'error' }
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetFormChange = (field, value) => {
    setResetForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleResetPasswordVisibility = (field) => {
    setShowResetPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <BackgroundContainer>
      <Container maxWidth="sm" sx={{ display: "flex", justifyContent: "center" }}>
        <Fade in={true} timeout={800}>
          <LoginCard>
            <CardContent sx={{ p: 4 }}>
              {/* Header */}
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mb={2}>
                <Box
                  sx={{
                    width: 84,
                    height: 84,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 8px 24px rgba(10,20,50,0.16)",
                    background: "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                    mb: 1,
                  }}
                >
                  <img src="/logo.png" alt="Stefto" style={{ width: 56, height: 56 }} />
                </Box>

                <Typography variant="h6" fontWeight={700}>
                  Stefto — Secure File Transfer
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 420 }}>
                  Fast, secure file sharing for your team. Sign in or create an account to continue.
                </Typography>
              </Box>

              <Tabs value={tabValue} onChange={handleTabChange} centered sx={{ mb: 2 }}>
                <Tab label="Sign In" icon={<LoginIcon />} iconPosition="start" />
                <Tab label="Sign Up" icon={<PersonAdd />} iconPosition="start" />
              </Tabs>

              {(error || authError) && (
                <Fade in={!!(error || authError)}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error || authError}
                  </Alert>
                </Fade>
              )}

              {/* SIGN IN */}
              <TabPanel value={tabValue} index={0}>
                <Box component="form" onSubmit={loginEmail ? handleVerifyOtp : handleLogin}>
                  <StyledTextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={loginEmail}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loginEmail}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={togglePasswordVisibility} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {loginEmail && (
                    <StyledTextField
                      required
                      fullWidth
                      name="otp"
                      label="Enter OTP"
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <Box display="flex" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                    {/* Forgot password opens the modal */}
                    <Button onClick={openForgot} size="small" variant="text">
                      Forgot password?
                    </Button>
                  </Box>

                  <StyledButton
                    type={loginEmail ? "button" : "submit"}
                    fullWidth
                    variant="contained"
                    disabled={isLoginLoading}
                    onClick={loginEmail ? handleVerifyOtp : undefined}
                    startIcon={isLoginLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  >
                    {isLoginLoading ? (loginEmail ? "Verifying OTP..." : "Signing In...") : loginEmail ? "Verify OTP" : "Sign In"}
                  </StyledButton>
                </Box>
              </TabPanel>

              {/* SIGN UP */}
              <TabPanel value={tabValue} index={1}>
                <Box component="form" onSubmit={signupEmail ? handleVerifySignupOtp : handleSignup}>
                  <StyledTextField
                    required
                    fullWidth
                    id="name"
                    label="Full Name"
                    name="name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={signupEmail}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    required
                    fullWidth
                    id="signup-username"
                    label="Username (@stefto.com)"
                    name="username"
                    autoComplete="username"
                    placeholder="Enter username"
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={signupEmail}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="body2" color="text.secondary">
                            @stefto.com
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    required
                    fullWidth
                    select
                    id="location"
                    label="Office Location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    disabled={signupEmail}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="gurugram">Gurugram</MenuItem>
                    <MenuItem value="delhi">Delhi</MenuItem>
                    <MenuItem value="pune">Pune</MenuItem>
                    <MenuItem value="noida">Noida</MenuItem>
                  </StyledTextField>
                  <StyledTextField
                    required
                    fullWidth
                    name="password"
                    label="Password (min 6 characters)"
                    type={showPassword ? "text" : "password"}
                    id="signup-password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={signupEmail}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={togglePasswordVisibility} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {signupEmail && (
                    <StyledTextField
                      required
                      fullWidth
                      name="signupOtp"
                      label="Enter Signup OTP"
                      type="text"
                      id="signup-otp"
                      value={signupOtp}
                      onChange={(e) => setSignupOtp(e.target.value)}
                    />
                  )}

                  <StyledButton
                    type={signupEmail ? "button" : "submit"}
                    fullWidth
                    variant="contained"
                    disabled={isSignupLoading}
                    onClick={signupEmail ? handleVerifySignupOtp : undefined}
                    startIcon={isSignupLoading ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                  >
                    {isSignupLoading ? (signupEmail ? "Verifying OTP..." : "Creating Account...") : signupEmail ? "Verify Signup OTP" : "Create Account"}
                  </StyledButton>
                </Box>
              </TabPanel>
            </CardContent>
          </LoginCard>
        </Fade>
      </Container>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onClose={closeForgot} maxWidth="sm" fullWidth>
        <DialogTitle>
          {forgotStep === 'email' ? 'Reset your password' : 'Enter OTP & New Password'}
        </DialogTitle>
        <DialogContent>
          {forgotStep === 'email' ? (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Enter the email address associated with your account. We'll send an OTP to reset your password.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Email address"
                type="email"
                fullWidth
                variant="outlined"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                We've sent a 6-digit OTP to <strong>{forgotEmail}</strong>. Enter the OTP and your new password below.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Enter OTP"
                type="text"
                fullWidth
                variant="outlined"
                value={resetForm.otp}
                onChange={(e) => handleResetFormChange('otp', e.target.value)}
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]*',
                }}
                helperText="Enter the 6-digit code sent to your email"
              />
              <TextField
                margin="dense"
                label="New Password"
                type={showResetPasswords.new ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={resetForm.newPassword}
                onChange={(e) => handleResetFormChange('newPassword', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleResetPasswordVisibility('new')}
                        edge="end"
                        size="small"
                      >
                        {showResetPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="dense"
                label="Confirm New Password"
                type={showResetPasswords.confirm ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={resetForm.confirmPassword}
                onChange={(e) => handleResetFormChange('confirmPassword', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleResetPasswordVisibility('confirm')}
                        edge="end"
                        size="small"
                      >
                        {showResetPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForgot} disabled={forgotLoading}>
            Cancel
          </Button>
          {forgotStep === 'email' ? (
            <Button
              onClick={handleForgotEmailSubmit}
              variant="contained"
              disabled={forgotLoading || !forgotEmail.trim()}
              startIcon={forgotLoading ? <CircularProgress size={18} /> : <EmailIcon />}
            >
              {forgotLoading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setForgotStep('email')}
                disabled={forgotLoading}
                startIcon={<Refresh />}
              >
                Back
              </Button>
              <Button
                onClick={handlePasswordReset}
                variant="contained"
                disabled={forgotLoading || !resetForm.otp.trim() || !resetForm.newPassword || !resetForm.confirmPassword}
                startIcon={forgotLoading ? <CircularProgress size={18} /> : <Lock />}
              >
                {forgotLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

    </BackgroundContainer>
  );
}

export default SimpleLogin;
